import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { uuid } from "@xapp/chat-widget-core";
import { Response, SuggestionTypes } from "stentor-models";
import { StentorApi, wasDroppedAfterFinal } from "../../api";
import { FormWidgetConfig } from "../../config";
import { useSearchStore } from "../../store";
import { getActionLabel } from "../../utils/getActionLabel";
import { log, err } from "../../utils";
import { getAllTrackingParameters } from "../../utils/tracking";
import { SearchWidgetConfigContext } from "../../utils/SearchWidgetConfigContext";
import { useLoading } from "../../utils/useLoading";
import { useTrustedForm } from "../../utils/useTrustedForm";
import { getMetaCookies } from "../../utils/metaCookies";
import { SpinnerIcon } from "../icons";
import { FormResponse } from "../FormResponse";
import { FormNextData, FormStepUpdateDisplay } from "../FormDisplay";
import { EventTrackingService } from "../../services/EventTrackingService";
import { EngagementTrigger } from "../../types/events";

import styles from "./FormWidget.module.scss";

/**
 * Interface for form step data in event tracking
 */
interface FormStep {
    name?: string;
    [key: string]: unknown;
}

/**
 * Interface for FORM display type used in event tracking
 */
interface FormDisplay {
    type: "FORM";
    steps?: FormStep[];
    [key: string]: unknown;
}

/**
 * Backoff between FORM_SUBMIT attempts; its length sets the retry count (3 attempts).
 *
 * Only FORM_SUBMIT is retried: it is the request that creates the lead, and losing it
 * loses the lead outright (issue #1373). StentorApi.send already retries once on a
 * non-ok status, but a transport failure -- fetch rejecting, the case where nothing
 * reaches the server at all -- got no retry and no handler.
 *
 * Retrying a lead-creating POST can duplicate it if the request landed but the response
 * did not. `extras.eventId` is minted once per submission and resent unchanged on every
 * attempt (see getSubmitEventId), so the client side already carries a stable
 * idempotency key -- but nothing consumes it on this path yet: contact-capture-handler's
 * FormActionResponseData does not declare `extras` and never reads `attributes.data`, so
 * the id is dropped before the lead is built, and the lead write itself has no dedupe of
 * any kind. Plumbing it through and keying a dedupe on it is tracked separately.
 */
const SUBMIT_RETRY_DELAYS_MS = [1000, 2000];

const wait = (ms: number) =>
    new Promise((resolve) => {
        setTimeout(resolve, ms);
    });

async function dispatchWithRetry<T>(attempt: () => Promise<T>, delays: number[]): Promise<T> {
    let lastError: unknown;

    for (let i = 0; i <= delays.length; i++) {
        if (i > 0) {
            await wait(delays[i - 1]);
        }

        try {
            return await attempt();
        } catch (error) {
            lastError = error;
            err(`FORM_SUBMIT attempt ${i + 1} of ${delays.length + 1} failed: ${error}`);
        }
    }

    throw lastError;
}

export const AUDIO_WAIT_NOTHING = 0,
    AUDIO_WAIT_CLOSE = 1,
    AUDIO_WAIT_MINIMIZE = 2;

export interface FormWidgetProps {
    readonly config: FormWidgetConfig;
    /**
     * Optional initial response to display
     */
    readonly response?: Response;

    readonly docked?: boolean;
    /**
     * Rendering inside a container the host page provided and sized (see `isEmbedded`).
     *
     * Drops the framing that makes the widget read as a floating card -- the desktop
     * max-width, border, radius and shadow -- so it fills the customer's div instead.
     */
    readonly embedded?: boolean;
    /**
     * Zero-based step to open the form on, instead of the first.
     *
     * For previews that want to show a particular step -- get.xapp.ai's booking
     * preview opens on the date and time step, because the calendar is the part
     * worth showing. Applied once, when the form arrives; an index the form has
     * no step for is ignored.
     */
    readonly initialStep?: number;
    /**
     * Open the date/time step on the soonest day that has times, rather than on
     * "Select a date to see the available times."
     *
     * For a preview that lands straight on that step: an empty column beside a
     * calendar reads as a form with nothing to offer. Off by default -- on a real
     * booking page a preselected date is one the customer never chose.
     */
    readonly preselectFirstAvailableDate?: boolean;
    /**
     * True when the form was activated by a proactive auto-open trigger
     * (delay/scroll%/exit-intent/inactivity) rather than a genuine user
     * action. Used to keep the `engaged` signal limited to real engagement.
     */
    readonly openedByTrigger?: boolean;
    /**
     * Label of a chip to auto-select after the form loads.
     * If the first response contains chips/suggestions, the form will
     * automatically click the chip matching this label.
     */
    readonly preselectChipLabel?: string;
    /**
     * Callback when preselectChipLabel has been used (to clear it)
     */
    readonly onPreselectChipUsed?: () => void;
    /**
     * Callback invoked when the user submits the final form step.
     *
     * Useful for firing external conversion tracking (e.g. a Meta Pixel
     * `Lead` event) from the embedding page. It receives ONLY a non-PII
     * `eventId` — the same id sent to the backend with the lead — so a
     * browser-side pixel event can be deduplicated against the server-side
     * Conversions API event. The submitted payload (which contains PII) is
     * deliberately never exposed to third-party trackers.
     */
    readonly onSubmit?: (eventId?: string) => void;
}

export const FormWidget: FC<FormWidgetProps> = (props) => {
    const { config } = props;

    return (
        <SearchWidgetConfigContext.Provider value={config}>
            <FormWidgetRaw
                docked={props.docked}
                embedded={props.embedded}
                initialStep={props.initialStep}
                preselectFirstAvailableDate={props.preselectFirstAvailableDate}
                openedByTrigger={props.openedByTrigger}
                preselectChipLabel={props.preselectChipLabel}
                onPreselectChipUsed={props.onPreselectChipUsed}
                onSubmit={props.onSubmit}
                response={props.response}
            />
        </SearchWidgetConfigContext.Provider>
    );
};

const FormWidgetRaw: FC<
    Pick<
        FormWidgetProps,
        | "docked"
        | "embedded"
        | "initialStep"
        | "preselectFirstAvailableDate"
        | "openedByTrigger"
        | "response"
        | "preselectChipLabel"
        | "onPreselectChipUsed"
        | "onSubmit"
    >
> = (props) => {
    const {
        docked,
        embedded,
        initialStep,
        preselectFirstAvailableDate,
        openedByTrigger,
        preselectChipLabel,
        onPreselectChipUsed,
        onSubmit,
    } =
        props;

    const [state, dispatch] = useSearchStore();
    const { loading, run } = useLoading();

    const { query, sendRequest, active } = state;

    const [response, setResponse] = useState<Response | undefined>(props.response);

    // A FORM_STEP_UPDATE display pulled out of a (fire-and-forget) FORM_SUBMIT
    // response, e.g. a booking-widget handoff step's externalWidget.config. Each
    // detected update is a fresh object from the API response, so FormDisplay's
    // effect on this prop always fires for a genuine new update.
    const [stepUpdate, setStepUpdate] = useState<FormStepUpdateDisplay | undefined>(undefined);

    const [audioOn, setAudioOn] = useState<boolean>(false);

    const [waitingOnAudioToFinish, setwaitingOnAudioToFinish] =
        useState<number>(AUDIO_WAIT_NOTHING);

    // why don't we just pass it in as a prop?
    const config = useContext(SearchWidgetConfigContext);

    // TrustedForm injects a third-party script that begins certifying the
    // session on mount. A preview must not generate a certificate with
    // ActiveProspect for a visit that isn't a real lead, so demo mode withholds
    // the script URL and the hook no-ops.
    const getTrustedFormData = useTrustedForm(
        config.api.demoMode ? undefined : config.trustedForm?.scriptUrl
    );

    const api = useMemo(() => {
        return StentorApi.getApi({
            key: config.api.key,
            url: config.api.url,
            intentId: config.intentId,
            // Carried through so the preview guarantee lives in one place.
            demoMode: config.api.demoMode,
            // prefetchQuery: config.autoGreeting
        });
    }, [config]);

    // Event tracking service - initialize with session and form info
    const eventTracker = useRef<EventTrackingService | null>(null);
    const formData = useRef<Record<string, unknown>>({});
    // Ref to sync current step's live data (before step completion)
    const currentStepDataRef = useRef<Record<string, unknown>>({});
    const currentStepRef = useRef<number>(0);
    const formSubmittedRef = useRef<boolean>(false);
    // Guards the docked auto-mount request so it fires exactly once per mount. See the effect below.
    // This never resets, which is correct only because `docked` is fixed for a mounted instance
    // (it is derived once in Form.tsx from config.autoOpen and does not toggle). If `docked` ever
    // becomes a runtime-togglable prop, this guard would suppress the legitimate second request and
    // must reset on the docked false->true transition.
    const dockedRequestSent = useRef<boolean>(false);

    // One id per submission, shared between the FORM_SUBMIT payload (sent to the
    // backend, which forwards it to Meta's Conversions API) and the browser Pixel
    // `Lead` event via onSubmit — so Meta deduplicates the two. Lazily created so
    // whichever submit handler runs first mints it and the other reuses it.
    const submitEventIdRef = useRef<string | undefined>(undefined);
    const getSubmitEventId = useCallback((): string => {
        const existing = submitEventIdRef.current;
        if (existing) {
            return existing;
        }
        const id = uuid();
        submitEventIdRef.current = id;
        return id;
    }, []);
    // Ref to store form display info for event tracking (avoids useEffect dependency on response)
    const formDisplayRef = useRef<FormDisplay | null>(null);
    // Ref to store the current form's id for event tracking (mirrors eventTracker's private formId)
    const formIdRef = useRef<string>("unknown");
    // Flag to prevent race condition where timeout fires after close/submit
    const abandonEventSentRef = useRef<boolean>(false);
    // Fires the `form_open` event at most once per mounted widget instance. Without this guard,
    // any intentRequest that resolves to a FORM display re-fires WIDGET_FORM_OPEN - e.g. a
    // chip click (handleActionClick) routed back through intentRequest after the form already
    // opened once. Each re-fire still carries full tracking metadata, but it inflates the OPEN
    // denominator the bounce/conversion funnel is measured against. See issue #1504.
    const formOpenSentRef = useRef<boolean>(false);
    // Fires the `engaged` event at most once per session (first genuine user action)
    const engagementSentRef = useRef<boolean>(false);
    const trackEngagement = useCallback((trigger: EngagementTrigger) => {
        if (!eventTracker.current || engagementSentRef.current) {
            return;
        }
        engagementSentRef.current = true;
        eventTracker.current.trackEngaged({
            formName: formIdRef.current,
            trigger,
            firstStepName: formDisplayRef.current?.steps?.[0]?.name,
        });
    }, []);

    const handleCancel = useCallback(() => {
        setResponse(undefined);
        dispatch({
            type: "new-search",
        });
        dispatch({
            type: "set-modal-state",
            active: false,
            minimized: true,
            sendRequest: false,
        });
    }, [dispatch]);

    const handleClose = useCallback(() => {
        // Track close and partial_data events if form wasn't submitted and abandon event not already sent.
        // Note: These are intentionally fire-and-forget (not awaited) to avoid blocking the UI.
        // The close event captures the UI action, while partial_data captures abandonment analytics
        // with abandonmentReason for funnel analysis. Both events serve different purposes.
        if (eventTracker.current && !formSubmittedRef.current && !abandonEventSentRef.current) {
            const form = formDisplayRef.current;
            if (form) {
                const totalSteps = form.steps?.length || 1;
                const currentStep = currentStepRef.current;
                // Merge completed step data with current step's live data
                const allFormData = { ...formData.current, ...currentStepDataRef.current };
                const hasData = Object.keys(allFormData).length > 0;

                // Send close event (captures the UI close action)
                eventTracker.current.trackClose({
                    lastStepName: form.steps?.[currentStep]?.name || "unknown",
                    lastStepIndex: currentStep,
                    totalStepsCompleted: currentStep,
                    totalSteps: totalSteps,
                    formData: hasData ? allFormData : undefined,
                });

                // Send partial_data event (captures abandonment with reason for funnel analysis)
                eventTracker.current.trackPartialData({
                    lastStepName: form.steps?.[currentStep]?.name || "unknown",
                    lastStepIndex: currentStep,
                    totalStepsCompleted: currentStep,
                    totalSteps: totalSteps,
                    partialData: hasData ? allFormData : undefined,
                    abandonmentReason: "widget_close",
                });

                abandonEventSentRef.current = true;
            }
        }

        setResponse(undefined);
        dispatch({
            type: "new-search",
        });
        dispatch({
            type: "set-modal-state",
            active: false,
            minimized: false,
            sendRequest: false,
        });
        api.channelActionRequest({ data: { result: {}, step: "", form: "" } }, "FORM_CLOSE").then(
            (closeResponse) => {
                log(
                    `ACTION Request (FORM_CLOSE). Data: Response: ${JSON.stringify(closeResponse)}`
                );
                setwaitingOnAudioToFinish(AUDIO_WAIT_NOTHING);
            }
        );
    }, [api, dispatch]);

    // Extract and store tracking parameters on mount
    // Note: This is safe to run even if tracking params were already extracted
    // (e.g., by contact-app). The function is idempotent and will just refresh
    // the stored values with the same data from the URL.
    useEffect(() => {
        const trackingParameters = getAllTrackingParameters(window.location.href, {
            persistToLocalStorage: true,
            persistenceDays: 30,
            preferUrlParameters: true,
        });

        // Log tracking parameters for debugging (development only)
        if (process.env.NODE_ENV === "development" && Object.keys(trackingParameters).length > 0) {
            log("[FormWidget] Captured tracking parameters:", trackingParameters);
        }
    }, []);

    // Track partial_data on page unload using sendBeacon for reliable delivery
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Merge completed step data with current step's live data
            const allFormData = { ...formData.current, ...currentStepDataRef.current };

            // Track abandon event if form wasn't submitted and no abandon event sent yet
            if (eventTracker.current && !formSubmittedRef.current && !abandonEventSentRef.current) {
                const form = formDisplayRef.current;
                if (form) {
                    const totalSteps = form.steps?.length || 1;
                    const currentStep = currentStepRef.current;

                    eventTracker.current.trackPartialDataBeacon({
                        lastStepName: form.steps?.[currentStep]?.name || "unknown",
                        lastStepIndex: currentStep,
                        partialData: allFormData,
                        totalStepsCompleted: currentStep,
                        totalSteps: totalSteps,
                        completionPercentage: Math.round((currentStep / totalSteps) * 100),
                        abandonmentReason: "close_tab",
                    });
                    abandonEventSentRef.current = true;
                }
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, []);

    // Track partial_data when tab is hidden until midnight (abandoned tab scenario)
    useEffect(() => {
        let midnightTimeoutId: ReturnType<typeof setTimeout> | null = null;

        const calculateMsUntilMidnight = (): number => {
            const now = new Date();
            const midnight = new Date(now);
            midnight.setHours(24, 0, 0, 0); // Next midnight
            return midnight.getTime() - now.getTime();
        };

        const sendTimeoutEvent = () => {
            // Merge completed step data with current step's live data
            const allFormData = { ...formData.current, ...currentStepDataRef.current };

            // Check all flags to prevent race conditions
            if (eventTracker.current && !formSubmittedRef.current && !abandonEventSentRef.current) {
                const form = formDisplayRef.current;
                if (form) {
                    const totalSteps = form.steps?.length || 1;
                    const currentStep = currentStepRef.current;

                    // Use beacon for reliable delivery
                    eventTracker.current.trackPartialDataBeacon({
                        lastStepName: form.steps?.[currentStep]?.name || "unknown",
                        lastStepIndex: currentStep,
                        partialData: allFormData,
                        totalStepsCompleted: currentStep,
                        totalSteps: totalSteps,
                        completionPercentage: Math.round((currentStep / totalSteps) * 100),
                        abandonmentReason: "timeout",
                    });

                    // Mark abandon event sent to prevent duplicate events
                    abandonEventSentRef.current = true;
                }
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Tab became hidden - start timer until midnight
                const msUntilMidnight = calculateMsUntilMidnight();
                midnightTimeoutId = setTimeout(sendTimeoutEvent, msUntilMidnight);
            } else {
                // Tab became visible - cancel the midnight timer
                if (midnightTimeoutId) {
                    clearTimeout(midnightTimeoutId);
                    midnightTimeoutId = null;
                }
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            if (midnightTimeoutId) {
                clearTimeout(midnightTimeoutId);
            }
        };
    }, []);

    const intentRequest = useCallback(
        (
            request: { query?: string; intentId?: string },
            options?: { engagementTrigger?: EngagementTrigger }
        ) => {
            dispatch({
                query: request.query,
                intentId: request.intentId,
                type: "set-query",
            });

            run(() =>
                api.intentRequest(request).then((intentResponse) => {
                    setResponse(intentResponse);
                    dispatch({
                        type: "set-response",
                        response: intentResponse,
                        query: request.query,
                        intentId: request.intentId,
                    });

                    // Initialize event tracker when form is loaded
                    const formDisplay = intentResponse?.displays?.find((d) => {
                        const display = d as unknown as { type?: string };
                        return display?.type === "FORM";
                    }) as FormDisplay | undefined;
                    if (formDisplay) {
                        formDisplayRef.current = formDisplay;
                        // Access session via casting through unknown (session is private)
                        const apiAny = api as unknown as { session?: { sessionId?: string } };
                        const sessionId = apiAny.session?.sessionId || "unknown";
                        const formId = (formDisplay as { name?: string }).name || "unknown";
                        formIdRef.current = formId;
                        eventTracker.current = new EventTrackingService(api, sessionId, formId);

                        // Track form_open event once per mounted widget instance - the first
                        // genuine surfacing of the form (docked render, trigger-activated open,
                        // or manual open). A later intentRequest resolving to a FORM display
                        // again (e.g. a chip click) is not a new surfacing and must not re-fire
                        // WIDGET_FORM_OPEN. See `engaged` for genuine user intent.
                        if (!formOpenSentRef.current) {
                            formOpenSentRef.current = true;
                            const totalSteps = formDisplay.steps?.length || 1;
                            const firstStepName = formDisplay.steps?.[0]?.name;
                            eventTracker.current.trackFormOpen({
                                formName: formId,
                                totalSteps,
                                firstStepName,
                                lastStepIndex: 0,
                            });
                        }

                        if (options?.engagementTrigger) {
                            trackEngagement(options.engagementTrigger);
                        }
                    }
                })
            );
        },
        [api, dispatch, run, trackEngagement]
    );

    const channelActionRequest = useCallback(
        (data: any, action: string): Promise<void> => {
            const dispatchOnce = () =>
                api.channelActionRequest(data, action).then((response) => {
                    log(
                        `ACTION Request (${action}). Data: ${JSON.stringify(
                            data
                        )} Response: ${JSON.stringify(response)}`
                    );

                    // A submit suppressed because the session already saw a final one
                    // never reached the server. That is usually correct duplicate
                    // protection, but it is also how a wrongly-latched session loses a
                    // lead in silence, so say so rather than logging it as a success.
                    if (wasDroppedAfterFinal(response)) {
                        err(
                            `ACTION Request (${action}) was suppressed: this session already sent a final submit.`
                        );
                    }

                    if (response.outputSpeech || response.displays) {
                        dispatch({
                            type: "set-response",
                            response,
                            query: "",
                        });
                    }

                    // FormDisplay awaits the FORM_SUBMIT dispatch, but only to learn
                    // whether it failed -- it still advances on click and does not read
                    // a return value. So a FORM_STEP_UPDATE in the response -- e.g. a
                    // booking-widget handoff step's externalWidget.config -- is surfaced
                    // back to FormDisplay via this prop rather than by threading a
                    // return value through onActionResponse.
                    if (action === "FORM_SUBMIT" && response.displays) {
                        const update = (response.displays as unknown as FormStepUpdateDisplay[]).find(
                            (display) => display?.type === "FORM_STEP_UPDATE"
                        );
                        if (update) {
                            setStepUpdate(update);
                        }
                    }

                    // Server side context (usually session params)
                    if (response.context?.active && response.context.active.length > 0) {
                        const context = response.context.active[0].parameters || {};

                        dispatch({
                            type: "set-context",
                            context,
                        });
                    }

                    if (!!data.autoClose) {
                        setwaitingOnAudioToFinish(AUDIO_WAIT_CLOSE);
                    } else if (!!data.autoMinimize) {
                        setwaitingOnAudioToFinish(AUDIO_WAIT_MINIMIZE);
                    } else {
                        setwaitingOnAudioToFinish(AUDIO_WAIT_NOTHING);
                    }
                });

            if (action === "FORM_SUBMIT") {
                // Rejects once the retries are exhausted. FormDisplay awaits this and
                // turns a rejection into a visible error the user can retry from --
                // before, it was an unhandled rejection and the lead just vanished.
                return run(() => dispatchWithRetry(dispatchOnce, SUBMIT_RETRY_DELAYS_MS));
            }

            // Every other action stays fire-and-forget, but is no longer allowed to
            // become an unhandled rejection.
            return run(dispatchOnce).catch((error) => {
                err(`ACTION Request (${action}) failed: ${error}`);
            });
        },
        [api, dispatch, run]
    );

    useEffect(() => {
        // if we need to send, it is active and we have an autoGreeting or intentId
        //
        // The dockedRequestSent guard is load-bearing. This effect dispatches set-modal-state
        // below, which flips `active` false -> true, and `active` is in the dep array — so the
        // effect re-runs. The non-docked branch is protected because that same dispatch sets
        // sendRequest to false, but `docked` short-circuits the condition, so a docked widget
        // fired intentRequest (and therefore trackFormOpen) twice on every mount.
        if (
            (docked && !dockedRequestSent.current) ||
            (sendRequest && active && (config.autoGreeting || config.intentId))
        ) {
            if (docked) {
                dockedRequestSent.current = true;
            }
            api.resetSession();
            // Docked auto-mount and proactive auto-open triggers are impressions,
            // not user intent - only a genuine, non-auto-triggered activation counts
            // as a manual open for the `engaged` signal.
            const engagementTrigger: EngagementTrigger | undefined =
                !docked && !openedByTrigger ? "manual_open" : undefined;
            intentRequest(
                { query: config.autoGreeting, intentId: config.intentId },
                { engagementTrigger }
            );
            dispatch({
                type: "set-modal-state",
                active: true,
                minimized: false,
                sendRequest: false,
            });
        }
    }, [
        docked,
        openedByTrigger,
        sendRequest,
        active,
        intentRequest,
        dispatch,
        config.autoGreeting,
        api,
        config.intentId,
    ]);

    useEffect(() => {
        if (!audioOn) {
            if (waitingOnAudioToFinish === AUDIO_WAIT_CLOSE) {
                setResponse(undefined);
                dispatch({
                    type: "new-search",
                });
                dispatch({
                    type: "set-modal-state",
                    active: false,
                    minimized: false,
                    sendRequest: false,
                });
                setwaitingOnAudioToFinish(AUDIO_WAIT_NOTHING);
            } else if (waitingOnAudioToFinish === AUDIO_WAIT_MINIMIZE) {
                setResponse(undefined);
                dispatch({
                    type: "new-search",
                });
                dispatch({
                    type: "set-modal-state",
                    active: false,
                    minimized: true,
                    sendRequest: false,
                });
                setwaitingOnAudioToFinish(AUDIO_WAIT_NOTHING);
            }
        }
    }, [audioOn, dispatch, waitingOnAudioToFinish]);

    const handleActionClick = useCallback(
        (item: SuggestionTypes) => {
            const text = getActionLabel(item);
            // A chip click is always a genuine user action, whether or not the
            // form was originally opened manually.
            intentRequest({ query: text }, { engagementTrigger: "chip_click" });
        },
        [intentRequest]
    );

    // Auto-select a chip based on preselectChipLabel when response has suggestions
    useEffect(() => {
        if (!preselectChipLabel || !response) {
            return;
        }

        // Look for suggestions in the response - outputSpeech can be string or ResponseOutput
        const outputSpeech = response.outputSpeech;
        const suggestions =
            typeof outputSpeech === "object" && outputSpeech !== null
                ? outputSpeech.suggestions
                : undefined;
        if (!suggestions || suggestions.length === 0) {
            return;
        }

        // Find a chip matching the preselectChipLabel
        const matchingChip = suggestions.find(
            (chip) => getActionLabel(chip).toLowerCase() === preselectChipLabel.toLowerCase()
        );

        if (matchingChip) {
            log(`Auto-selecting chip: ${getActionLabel(matchingChip)}`);
            // Small delay to ensure UI is rendered before clicking
            setTimeout(() => {
                handleActionClick(matchingChip);
            }, 100);
        }

        // Clear the preselect label so we don't re-trigger
        if (onPreselectChipUsed) {
            onPreselectChipUsed();
        }
    }, [response, preselectChipLabel, handleActionClick, onPreselectChipUsed]);

    const handleActionResponse = useCallback(
        (data: unknown, action: string) => {
            if (!action) {
                action = (data as any).action;
            }
            // Tell the server the visitors TZ offset
            (data as any).timezoneOffset = new Date().getTimezoneOffset();

            if (action === "FORM_SUBMIT") {
                const trustedFormData = getTrustedFormData();
                if (trustedFormData && (data as any).result) {
                    (data as any).result = { ...(data as any).result, ...trustedFormData };
                }
                // Carry the shared event id + Meta cookies so the backend can send
                // a deduplicated, high-match Conversions API Lead event.
                (data as any).extras = {
                    ...(data as any).extras,
                    eventId: getSubmitEventId(),
                    ...getMetaCookies(),
                };
            }

            // Returned so FormDisplay can await the FORM_SUBMIT dispatch and surface a
            // failure rather than losing the lead silently (issue #1373).
            return channelActionRequest(data, action);
        },
        [channelActionRequest, getTrustedFormData, getSubmitEventId]
    );

    const handleFormNext = useCallback(
        (data: FormNextData) => {
            return api.channelActionRequest(data, "FORM_NEXT");
        },
        [api]
    );

    const handleAudioChange = (isOn: boolean) => {
        setAudioOn(isOn);
    };

    // Tracking callbacks
    // Note: We intentionally fire BOTH specific events (next_step/previous_step) AND generic events (step_change).
    // - next_step/previous_step: Rich context for specific navigation analytics
    // - step_change: Unified view of all navigation for funnel analysis
    // This dual-event pattern is per the Widget Events API spec. May consolidate later based on analytics needs.
    const handleTrackInitialStep = useCallback((stepIndex: number) => {
        // The form opened straight on this step, so it is where the visitor
        // actually is. currentStepRef is otherwise only written by the
        // next/previous handlers, which need an explicit navigation to fire --
        // leaving it on 0 for a form opened via initialStep, and under-reporting
        // lastStepIndex and completionPercentage on every abandonment path.
        //
        // Deliberately not trackEngagement: nobody engaged with anything by
        // being shown a step.
        currentStepRef.current = stepIndex;
    }, []);

    const handleTrackNextStep = useCallback(
        (
            stepName: string,
            stepIndex: number,
            stepData: Record<string, unknown>,
            isLastStep: boolean
        ) => {
            // Update form data ref and reset current step data for the new step
            formData.current = { ...formData.current, ...stepData };
            currentStepDataRef.current = {}; // Reset live data for new step
            currentStepRef.current = stepIndex + 1; // Moving to next step

            // Completing a step is a genuine user action, whether or not the
            // form was originally opened manually.
            trackEngagement("step_transition");

            if (eventTracker.current) {
                const form = formDisplayRef.current;
                const nextStepName = form?.steps?.[stepIndex + 1]?.name;
                const totalSteps = form?.steps?.length || 1;

                // Track next_step event (specific forward navigation with step data)
                eventTracker.current.trackNextStep({
                    completedStepName: stepName,
                    completedStepIndex: stepIndex,
                    nextStepName,
                    stepData,
                    isLastStep,
                });

                // Track step_change event (generic navigation for unified funnel view)
                if (nextStepName) {
                    eventTracker.current.trackStepChange({
                        fromStepName: stepName,
                        fromStepIndex: stepIndex,
                        toStepName: nextStepName,
                        toStepIndex: stepIndex + 1,
                        direction: "forward",
                        lastStepIndex: stepIndex + 1,
                        totalSteps,
                    });
                }
            }
        },
        [trackEngagement]
    );

    const handleTrackPreviousStep = useCallback(
        (fromStepName: string, fromStepIndex: number, toStepName: string, toStepIndex: number) => {
            // Update current step ref and reset current step data for the new step
            currentStepDataRef.current = {}; // Reset live data for new step
            currentStepRef.current = toStepIndex;

            if (eventTracker.current) {
                const totalSteps = formDisplayRef.current?.steps?.length || 1;

                // Track previous_step event (specific backward navigation)
                eventTracker.current.trackPreviousStep({
                    fromStepName,
                    fromStepIndex,
                    toStepName,
                    toStepIndex,
                    lastStepIndex: toStepIndex,
                    totalSteps,
                });

                // Track step_change event (generic navigation for unified funnel view)
                eventTracker.current.trackStepChange({
                    fromStepName,
                    fromStepIndex,
                    toStepName,
                    toStepIndex,
                    direction: "backward",
                    lastStepIndex: toStepIndex,
                    totalSteps,
                });
            }
        },
        []
    );

    const handleTrackSubmit = useCallback(
        (
            stepName: string,
            stepIndex: number,
            completedData: Record<string, unknown>,
            totalSteps: number
        ) => {
            // Guard against double-firing the submit event: a double-click before the
            // "submitting" UI state flushes, a re-render, or a retry can invoke this
            // callback more than once for the same submission. Only the first call
            // should ever track/notify.
            if (formSubmittedRef.current) {
                return;
            }
            formSubmittedRef.current = true;

            // Include TrustedForm cert URL in submit event if available
            const trustedFormData = getTrustedFormData();
            const dataWithTrustedForm = trustedFormData
                ? { ...completedData, ...trustedFormData }
                : completedData;

            formData.current = dataWithTrustedForm;

            if (eventTracker.current) {
                eventTracker.current.trackSubmit({
                    finalStepName: stepName,
                    finalStepIndex: stepIndex,
                    completedData: dataWithTrustedForm,
                    totalSteps,
                });
            }

            // Notify the embedding page that a submit happened (e.g. to fire
            // external conversion tracking). Deliberately passes no data: the
            // submitted payload contains PII (and the TrustedForm cert URL) and
            // must not be handed to third-party trackers.
            // ...unless this is a preview. onSubmit exists so the host can
            // fire external conversion tracking — a Meta Pixel Lead event, per
            // its own doc comment. That reaches a third-party ad platform, so
            // it is the most consequential signal demo mode has to suppress:
            // it is driven purely by UI completion and would otherwise fire for
            // a session that never touched the backend.
            if (onSubmit && !config.api.demoMode) {
                try {
                    onSubmit(getSubmitEventId());
                } catch (error) {
                    err("[FormWidget] onSubmit callback threw:", error);
                }
            }
        },
        [getTrustedFormData, onSubmit, getSubmitEventId, config.api.demoMode]
    );

    // The FORM_SUBMIT dispatch failed after its retries, so the lead was not created.
    // handleTrackSubmit above has already recorded the submit; this is the counter-signal
    // that keeps that from reading as a success (issue #1373). The formSubmittedRef guard
    // is deliberately not consulted -- the submit event it gated has already gone out,
    // and this event is what corrects it.
    const handleTrackSubmitFailure = useCallback(
        (stepName: string, stepIndex: number, totalSteps: number, reason: string) => {
            eventTracker.current?.trackSubmitFailure({
                finalStepName: stepName,
                finalStepIndex: stepIndex,
                totalSteps,
                reason,
            });
        },
        []
    );

    // Booking-widget handoff step tracking. formName/sessionId are carried on
    // every event via eventTracker's own metadata (see EventTrackingService),
    // matching how the other track* handlers above don't pass them explicitly.
    const handleTrackHandoffRendered = useCallback((stepName: string, provider: string) => {
        eventTracker.current?.trackHandoffRendered({ stepName, provider });
    }, []);

    const handleTrackHandoffNoRender = useCallback((stepName: string, provider: string) => {
        eventTracker.current?.trackHandoffNoRender({ stepName, provider });
    }, []);

    const handleTrackHandoffScheduled = useCallback((stepName: string, provider: string, payload?: unknown) => {
        eventTracker.current?.trackHandoffScheduled({ stepName, provider, payload });
    }, []);

    const handleTrackHandoffError = useCallback((stepName: string, provider: string) => {
        eventTracker.current?.trackHandoffError({ stepName, provider });
    }, []);

    const displayContainer: boolean = !!query || !!response;

    if (docked) {
        // we might want to put a wrapper around this for docked mode
        return (
            <div
                className={`${styles.xapp_form_widget} ${embedded ? styles.embedded : ""} ${
                    loading ? styles.loading : ""
                }`}>
                <div
                    className={`${styles["xapp_form_widget__content"]} ${
                        loading ? styles.loading : ""
                    }`}
                >
                    {response ? (
                        <FormResponse
                            response={response}
                            className={styles["xapp_form_widget__response"]}
                            onActionClick={handleActionClick}
                            onActionResponse={handleActionResponse}
                            onAudioChange={handleAudioChange}
                            loading={loading}
                            onTrackNextStep={handleTrackNextStep}
                            onTrackPreviousStep={handleTrackPreviousStep}
                            onTrackSubmit={handleTrackSubmit}
                            onTrackSubmitFailure={handleTrackSubmitFailure}
                            currentStepDataRef={currentStepDataRef}
                            initialStep={initialStep}
                            preselectFirstAvailableDate={preselectFirstAvailableDate}
                            onTrackInitialStep={handleTrackInitialStep}
                            onFormNext={handleFormNext}
                            stepUpdate={stepUpdate}
                            onTrackHandoffRendered={handleTrackHandoffRendered}
                            onTrackHandoffNoRender={handleTrackHandoffNoRender}
                            onTrackHandoffScheduled={handleTrackHandoffScheduled}
                            onTrackHandoffError={handleTrackHandoffError}
                        />
                    ) : (
                        <div className={styles["xapp_form_widget__spinner"]}>
                            <SpinnerIcon />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            className={`${styles.xapp_form_widget} ${embedded ? styles.embedded : ""} ${
                loading && !response ? styles.loading : ""
            }`}>
            {displayContainer && (
                <div
                    className={`${styles["xapp_form_widget__content"]} ${
                        loading && !response ? styles.loading : ""
                    }`}
                >
                    {!response && loading && (
                        <div className={styles["xapp_form_widget__spinner"]}>
                            <SpinnerIcon />
                        </div>
                    )}

                    {response && (
                        <FormResponse
                            response={response}
                            className={styles["xapp_form_widget__response"]}
                            onActionClick={handleActionClick}
                            onActionResponse={handleActionResponse}
                            onAudioChange={handleAudioChange}
                            loading={loading}
                            onTrackNextStep={handleTrackNextStep}
                            onTrackPreviousStep={handleTrackPreviousStep}
                            onTrackSubmit={handleTrackSubmit}
                            onTrackSubmitFailure={handleTrackSubmitFailure}
                            currentStepDataRef={currentStepDataRef}
                            initialStep={initialStep}
                            preselectFirstAvailableDate={preselectFirstAvailableDate}
                            onTrackInitialStep={handleTrackInitialStep}
                            onFormNext={handleFormNext}
                            stepUpdate={stepUpdate}
                            onTrackHandoffRendered={handleTrackHandoffRendered}
                            onTrackHandoffNoRender={handleTrackHandoffNoRender}
                            onTrackHandoffScheduled={handleTrackHandoffScheduled}
                            onTrackHandoffError={handleTrackHandoffError}
                        />
                    )}
                </div>
            )}

            {response && !!handleCancel && (
                <div className={styles["xapp_form_widget__close"]} onClick={handleClose}>
                    {"\u2715"}
                </div>
            )}
        </div>
    );
};
